import React from 'react';
import { Trophy, TrendingUp, TrendingDown, Minus } from 'lucide-react';

const LeaderboardCategory = ({ title, icon, entries, loading, emptyMessage }) => {
  const getTrendIcon = (change) => {
    if (!change || change === 0) return <Minus className="w-4 h-4 text-gray-400" />;
    if (change > 0) return <TrendingUp className="w-4 h-4 text-green-500" />;
    return <TrendingDown className="w-4 h-4 text-red-500" />;
  };

  const getPositionBadgeColor = (position) => {
    if (position === 1) return 'bg-gradient-to-r from-yellow-400 to-yellow-600 text-white';
    if (position === 2) return 'bg-gradient-to-r from-gray-300 to-gray-500 text-white';
    if (position === 3) return 'bg-gradient-to-r from-orange-400 to-orange-600 text-white';
    return 'bg-gradient-to-r from-purple-100 to-purple-200 text-purple-900';
  };

  if (loading) {
    return (
      <div className="bg-white rounded-2xl p-6 shadow-lg">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-8 h-8 bg-gray-200 rounded-lg animate-pulse" />
          <div className="h-6 w-48 bg-gray-200 rounded animate-pulse" />
        </div>
        <div className="space-y-3">
          {[1, 2, 3, 4, 5]?.map((i) => (
            <div key={i} className="flex items-center gap-4 p-3 bg-gray-50 rounded-xl animate-pulse">
              <div className="w-10 h-10 bg-gray-200 rounded-lg" />
              <div className="w-10 h-10 bg-gray-200 rounded-full" />
              <div className="flex-1">
                <div className="h-4 w-32 bg-gray-200 rounded mb-2" />
                <div className="h-3 w-24 bg-gray-200 rounded" />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (!entries || entries?.length === 0) {
    return (
      <div className="bg-white rounded-2xl p-6 shadow-lg">
        <div className="flex items-center gap-3 mb-6">
          {icon}
          <h2 className="text-xl font-bold text-gray-900">{title}</h2>
        </div>
        <div className="text-center py-12">
          <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
            <Trophy className="w-8 h-8 text-gray-400" />
          </div>
          <p className="text-gray-500">{emptyMessage}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-shadow duration-300">
      <div className="flex items-center gap-3 mb-6">
        {icon}
        <h2 className="text-xl font-bold text-gray-900">{title}</h2>
        <span className="ml-auto text-sm text-gray-500">{entries?.length} participants</span>
      </div>
      <div className="space-y-3">
        {entries?.map((entry) => (
          <div
            key={entry?.id}
            className="group relative flex items-center gap-4 p-4 rounded-xl bg-gradient-to-r from-purple-50 to-indigo-50 hover:from-purple-100 hover:to-indigo-100 transition-all duration-300 cursor-pointer"
          >
            {/* Position Badge */}
            <div className={`flex-shrink-0 w-12 h-12 rounded-lg flex items-center justify-center font-bold text-lg ${getPositionBadgeColor(entry?.position)}`}>
              {entry?.position}
            </div>

            {/* User Avatar */}
            <div className="flex-shrink-0">
              {entry?.profile_photo_url ? (
                <img
                  src={entry?.profile_photo_url}
                  alt={entry?.username || 'User'}
                  className="w-12 h-12 rounded-full border-2 border-white shadow-md object-cover"
                />
              ) : (
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-purple-400 to-indigo-500 flex items-center justify-center text-white font-bold text-lg shadow-md">
                  {(entry?.username || entry?.full_name || 'U')?.[0]?.toUpperCase()}
                </div>
              )}
            </div>

            {/* User Info */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <h3 className="font-semibold text-gray-900 truncate">
                  {entry?.username || entry?.full_name || 'Anonymous User'}
                </h3>
                {entry?.position <= 3 && (
                  <Trophy className={`w-4 h-4 ${
                    entry?.position === 1 ? 'text-yellow-500' :
                    entry?.position === 2 ? 'text-gray-400': 'text-orange-500'
                  }`} />
                )}
              </div>
              <div className="text-sm text-gray-600">
                {entry?.role === 'editor' && <span className="text-purple-600 font-medium">Educator</span>}
                {entry?.role === 'user' && <span className="text-blue-600 font-medium">Student</span>}
                {entry?.role === 'talent' && <span className="text-pink-600 font-medium">Talent</span>}
              </div>
            </div>

            {/* Stats Display */}
            <div className="text-right">
              {entry?.total_lifetime_xp !== undefined && (
                <div className="font-bold text-lg text-purple-600">
                  {entry?.total_lifetime_xp?.toLocaleString()} XP
                </div>
              )}
              {entry?.daily_streak !== undefined && (
                <div className="font-bold text-lg text-orange-500">
                  {entry?.daily_streak} days 🔥
                </div>
              )}
              {entry?.completionRate !== undefined && (
                <div className="font-bold text-lg text-green-600">
                  {entry?.completionRate?.toFixed(1)}%
                </div>
              )}
              {entry?.momentum !== undefined && (
                <div className="font-bold text-lg text-blue-600">
                  {entry?.momentum} pts
                </div>
              )}
              
              {/* Secondary stats */}
              <div className="text-xs text-gray-500 mt-1">
                {entry?.weeklyXpGain !== undefined && (
                  <span>+{entry?.weeklyXpGain} this week</span>
                )}
                {entry?.streakMilestone && (
                  <span>{entry?.streakMilestone}</span>
                )}
                {entry?.lessonsCompleted !== undefined && (
                  <span>{entry?.lessonsCompleted}/{entry?.totalLessons} completed</span>
                )}
                {entry?.recentAchievements !== undefined && (
                  <span>{entry?.recentAchievements} recent achievements</span>
                )}
              </div>
            </div>

            {/* Position change indicator */}
            {entry?.positionChange !== undefined && (
              <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
                {getTrendIcon(entry?.positionChange)}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default LeaderboardCategory;